import type { RouteTable } from "@hakili/app-shell";
import type { RoleBanque } from "@hakili/domain-types";

/**
 * `P0-UIK-017`, and `WF-BNK-01`: the two personas, their screens, and the
 * derivation from the role.
 *
 * **The table is canon's.** 03, 3.0 gives it literally , *"Analyste crédit:
 * Pipeline, Dossiers, workspace, Pilotage, Profil"* and *"Chef du crédit:
 * Décisions, Pipeline (équipe), Comité, Équipe, Pilotage, Politique, Profil"*
 * , and the landing screen is the first of the list, as in the console.
 *
 * **Danger sentence.** `WF-BNK-01` is explicit that the switcher is a
 * demonstration device: *"the persona is derived from the bank user's role;
 * the switcher exists only behind the `demo` flag"* (`P0-UIK-018`). An analyst
 * who can select `chef` sees the decisions queue and the credit policy , the
 * two screens that decide whether money moves. So the persona has exactly one
 * input, the role, and it comes from a JWT the server signed.
 *
 * **The workspace is not a screen of its own.** 03 lists "workspace" for the
 * analyst, and 4.6.4 serves it at `/dossiers/:id`: it is the dossier screen
 * opened on one file, with the document viewer and agent panel of 4.6.1. A
 * separate whitelist entry would let one be granted without the other, and
 * there is no sentence in canon that wants that.
 */

export const PERSONAS = ["analyste", "chef"] as const;

export type Persona = (typeof PERSONAS)[number];

/** The screens of 03, 3.0, as stable ids. */
export const ECRANS_BANQUE = [
  "pipeline",
  "dossiers",
  "decisions",
  "comite",
  "equipe",
  "pilotage",
  "politique",
  "profil",
] as const;

export type EcranBanque = (typeof ECRANS_BANQUE)[number];

export interface PersonaDefinition {
  /** The catalogue key. 6.5.1 keeps the French in `packages/i18n`. */
  readonly labelKey: string;
  /** Canon's order. The FIRST is the landing screen. */
  readonly ecrans: readonly EcranBanque[];
}

export const PERSONA_DEFINITIONS: Readonly<Record<Persona, PersonaDefinition>> = {
  // "Pipeline, Dossiers, workspace, Pilotage, Profil" , the workspace being
  // `/dossiers/:id`, which the `dossiers` screen already carries.
  analyste: {
    labelKey: "bank.persona.analyste",
    ecrans: ["pipeline", "dossiers", "pilotage", "profil"],
  },
  // "Décisions, Pipeline (équipe), Comité, Équipe, Pilotage, Politique,
  // Profil". "Pipeline (équipe)" is the same screen scoped to the team, which
  // 6.1.8's scope layer decides and a whitelist cannot.
  chef: {
    labelKey: "bank.persona.chef",
    ecrans: ["decisions", "pipeline", "comite", "equipe", "pilotage", "politique", "profil"],
  },
};

export function ecranDAtterrissage(persona: Persona): EcranBanque {
  const [premier] = PERSONA_DEFINITIONS[persona].ecrans;
  if (premier === undefined) throw new Error(`persona "${persona}" has no screens, so it lands nowhere`);
  return premier;
}

export const ecransDuPersona = (persona: Persona): ReadonlySet<EcranBanque> =>
  new Set(PERSONA_DEFINITIONS[persona].ecrans);

/** The routes of 4.6.4 for `bank-web`, each tagged with the screen it serves. */
export const ROUTES: RouteTable = {
  landingScreen: "pipeline",
  routes: [
    { path: "/pipeline", screen: "pipeline" },
    { path: "/dossiers", screen: "dossiers" },
    // The workspace: the dossier opened on one file (4.6.1's viewer and agent
    // panel). Same screen, same grant.
    { path: "/dossiers/:id", screen: "dossiers" },
    { path: "/decisions", screen: "decisions" },
    { path: "/comite", screen: "comite" },
    { path: "/comite/:sessionId", screen: "comite" },
    { path: "/equipe", screen: "equipe" },
    { path: "/pilotage", screen: "pilotage" },
    { path: "/politique", screen: "politique" },
    { path: "/profil", screen: "profil" },
  ],
};

export const routesPourPersona = (persona: Persona): RouteTable => ({
  ...ROUTES,
  landingScreen: ecranDAtterrissage(persona),
});

/** The landing PATH, derived from the table so the mapping is written once. */
export function cheminDAtterrissage(persona: Persona): string {
  const ecran = ecranDAtterrissage(persona);
  const route = ROUTES.routes.find((candidate) => candidate.screen === ecran);
  if (route === undefined) throw new Error(`no route serves the landing screen "${ecran}"`);
  return route.path;
}

/**
 * `WF-BNK-01`: the persona is derived from the role.
 *
 * Exhaustive by type over `RoleBanque`, so a third bank role added to the
 * vocabulary fails the build here rather than arriving as an analyst who logs
 * in and sees nothing.
 */
export const PERSONA_PAR_ROLE: Readonly<Record<RoleBanque, Persona>> = {
  chefCredit: "chef",
  analysteCredit: "analyste",
};

export const personaPourRole = (role: RoleBanque): Persona => PERSONA_PAR_ROLE[role];
